/**
 * 掲示板の表示メタ定数（Badge variant / 緊急度カラー / 期日 chip）。
 * 仕様書: docs/02_work/done/plan_board_feature.md §1-3（既存カラーセマンティクス準拠）
 *
 * ラベル文字列は @goenhub/shared の BOARD_POST_TYPE_LABEL / BOARD_URGENCY_LABEL を使う。
 */
import type {
  BoardPost,
  BoardPostType,
  BoardRecipientState,
  BoardUrgency,
} from '@goenhub/shared';

export type BadgeVariant =
  | 'default'
  | 'draft'
  | 'review'
  | 'approved'
  | 'sent'
  | 'danger'
  | 'accent';

/** タイプの Badge variant: 決定確認=purple / 作業依頼=青 / 共有事項=灰。 */
export const BOARD_TYPE_VARIANT: Record<BoardPostType, BadgeVariant> = {
  decision: 'review',
  task: 'accent',
  info: 'default',
};

/** 緊急度の Badge variant: 高=red / 中=amber / 低=灰。 */
export const BOARD_URGENCY_VARIANT: Record<BoardUrgency, BadgeVariant> = {
  high: 'danger',
  medium: 'draft',
  low: 'default',
};

/** 一覧行の左カラーバー色。低はバーなし (null)。 */
export const BOARD_URGENCY_BAR_COLOR: Record<BoardUrgency, string | null> = {
  high: 'var(--accent-red)',
  medium: 'var(--accent-amber)',
  low: null,
};

/**
 * 宛先ステータスの Badge variant (§1-3):
 * 未確認=amber / 既読=cyan / 保留=purple / 決定・完了・確認済=green / 却下・キャンセル=灰。
 */
export const BOARD_STATE_VARIANT: Record<BoardRecipientState, BadgeVariant> = {
  unread: 'draft',
  read: 'sent',
  onhold: 'review',
  decided: 'approved',
  done: 'approved',
  acknowledged: 'approved',
  rejected: 'default',
  cancelled: 'default',
};

/** 投稿全体ステータス（表示専用のクライアント導出。plan §2-2-2）。 */
export interface BoardOverallStatus {
  kind: 'closed' | 'done' | 'open';
  label: string;
  variant: BadgeVariant;
}

/**
 * 投稿全体ステータスの導出規則 (§2-2-2):
 * - closedAt != null            → 「クローズ」(灰)
 * - audience === 'all'          → null（追跡なし・chip 非表示）
 * - selected && isOpen          → 「対応中 · 残りN名」(青)
 * - selected && !isOpen         → 「対応完了」(green)
 */
export function boardOverallStatus(
  post: Pick<BoardPost, 'closedAt' | 'audience' | 'isOpen' | 'openRecipientUids'>,
): BoardOverallStatus | null {
  if (post.closedAt != null) {
    return { kind: 'closed', label: 'クローズ', variant: 'default' };
  }
  if (post.audience !== 'selected') return null;
  if (post.isOpen) {
    return {
      kind: 'open',
      label: `対応中 · 残り${(post.openRecipientUids ?? []).length}名`,
      variant: 'accent',
    };
  }
  return { kind: 'done', label: '対応完了', variant: 'approved' };
}

/** 今日の 'YYYY-MM-DD' (ローカル = JST 運用)。dueDate の文字列比較に使う。 */
export function todayYmd(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 'YYYY-MM-DD' → 'M/D' 表示。 */
export function formatDueMd(dueDate: string): string {
  const [, m, d] = dueDate.split('-');
  return `${Number(m)}/${Number(d)}`;
}

/**
 * 期日 chip の表示情報。
 * 超過=danger「期日超過 M/D」/ 当日・前日=amber(draft) / それ以外=default。
 */
export function boardDueChip(dueDate: string): { label: string; variant: BadgeVariant } {
  const today = todayYmd();
  if (dueDate < today) {
    return { label: `期日超過 ${formatDueMd(dueDate)}`, variant: 'danger' };
  }
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  const tomorrowYmd = `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}`;
  if (dueDate <= tomorrowYmd) {
    return { label: `期日 ${formatDueMd(dueDate)}`, variant: 'draft' };
  }
  return { label: `期日 ${formatDueMd(dueDate)}`, variant: 'default' };
}
